import { useState, useContext, useEffect } from "react";
import { UserContext, API } from "@/App";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Hash, RotateCw, History, Volume2, VolumeX, Zap, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { celebrateWin, soundEffects } from "@/lib/celebrations";

export const KenoPage = () => {
  const { user, refreshBalance, token } = useContext(UserContext);
  const [betAmount, setBetAmount] = useState(100);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);

  useEffect(() => {
    soundEffects.setEnabled(soundEnabled);
  }, [soundEnabled]);

  const toggleNumber = (num) => {
    if (loading) return;
    
    if (selectedNumbers.includes(num)) {
      setSelectedNumbers(selectedNumbers.filter(n => n !== num));
    } else if (selectedNumbers.length < 10) {
      setSelectedNumbers([...selectedNumbers, num]);
    } else {
      toast.error("Maximum 10 numbers allowed");
    }
  };

  const clearSelection = () => {
    setSelectedNumbers([]);
    setLastResult(null);
  };

  const autoSelect = (count) => {
    const available = Array.from({ length: 40 }, (_, i) => i + 1);
    const shuffled = available.sort(() => Math.random() - 0.5);
    setSelectedNumbers(shuffled.slice(0, count));
  };

  const playGame = async () => {
    if (betAmount <= 0 || betAmount > (user?.balance || 0)) {
      toast.error("Invalid bet amount");
      return;
    }
    if (selectedNumbers.length === 0) {
      toast.error("Select at least 1 number");
      return;
    }

    setLoading(true);
    soundEffects.playSpin();

    try {
      const response = await axios.post(`${API}/games/keno/play`, {
        game_type: "keno",
        bet_amount: betAmount,
        bet_data: { numbers: selectedNumbers }
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` }
      });

      setLastResult(response.data);
      setHistory(prev => [response.data, ...prev.slice(0, 9)]);

      if (response.data.won) {
        toast.success(`${response.data.hits} hits! Won ₹${response.data.winnings.toFixed(2)} at ${response.data.multiplier}x!`);
        celebrateWin(response.data.multiplier, response.data.winnings);
      } else {
        toast.error(`${response.data.hits} hits - not enough to win`);
        soundEffects.playLoss();
      }

      await refreshBalance();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Game error");
    } finally {
      setLoading(false);
    }
  };

  // Payout table
  const payoutTable = {
    1: { 1: 3.5 },
    2: { 2: 9 },
    3: { 2: 2.5, 3: 25 },
    4: { 2: 1.5, 3: 5, 4: 50 },
    5: { 2: 1, 3: 3, 4: 15, 5: 100 },
    6: { 3: 2, 4: 5, 5: 25, 6: 250 },
    7: { 3: 1.5, 4: 3, 5: 10, 6: 50, 7: 500 },
    8: { 3: 1, 4: 2, 5: 5, 6: 25, 7: 100, 8: 1000 },
    9: { 4: 1.5, 5: 3, 6: 10, 7: 50, 8: 250, 9: 2500 },
    10: { 4: 1, 5: 2, 6: 5, 7: 25, 8: 100, 9: 500, 10: 5000 }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#FF6B00]/20 flex items-center justify-center">
              <Hash className="w-6 h-6 text-[#FF6B00]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white font-['Rajdhani']" data-testid="keno-title">Keno</h1>
              <p className="text-[#A1A1AA] text-sm">Pick up to 10 numbers and match the draw!</p>
            </div>
          </div>
          <button 
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="p-2 rounded-lg bg-white/5 text-white/60 hover:text-white"
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Number Grid */}
          <div className="lg:col-span-2 p-4 rounded-2xl bg-[#1A1B1F] border border-white/10">
            <div className="flex justify-between items-center mb-4">
              <span className="text-[#A1A1AA]">
                Selected: <span className="text-white font-bold">{selectedNumbers.length}/10</span>
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearSelection} className="border-white/10 text-white/60">
                  Clear
                </Button>
                <Button variant="outline" size="sm" onClick={() => autoSelect(5)} className="border-white/10 text-white/60">
                  Auto 5
                </Button>
                <Button variant="outline" size="sm" onClick={() => autoSelect(10)} className="border-white/10 text-white/60">
                  Auto 10
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-8 gap-2">
              {Array.from({ length: 40 }, (_, i) => i + 1).map(num => {
                const isSelected = selectedNumbers.includes(num);
                const isDrawn = lastResult?.drawn_numbers?.includes(num);
                const isHit = isSelected && isDrawn;

                return (
                  <button
                    key={num}
                    onClick={() => toggleNumber(num)}
                    disabled={loading}
                    className={`
                      w-full aspect-square rounded-lg font-bold text-lg transition-all
                      ${isHit 
                        ? 'bg-[#00E701] text-black scale-110 shadow-lg shadow-[#00E701]/30' 
                        : isDrawn 
                          ? 'bg-[#FFD600]/30 text-[#FFD600]'
                          : isSelected 
                            ? 'bg-[#FF6B00] text-black' 
                            : 'bg-white/5 text-white hover:bg-white/10'
                      }
                    `}
                    data-testid={`keno-num-${num}`}
                  >
                    {num}
                  </button>
                );
              })}
            </div>

            {/* Result Display */}
            {lastResult && (
              <div className="mt-4 p-4 rounded-xl bg-black/30">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-[#A1A1AA] text-sm">Drawn Numbers</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {lastResult.drawn_numbers.map(num => (
                        <span 
                          key={num}
                          className={`w-8 h-8 rounded flex items-center justify-center text-sm font-bold ${
                            selectedNumbers.includes(num) 
                              ? 'bg-[#00E701] text-black' 
                              : 'bg-[#FFD600]/20 text-[#FFD600]'
                          }`}
                        >
                          {num}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[#A1A1AA] text-sm">Hits</p>
                    <p className={`text-3xl font-bold ${lastResult.won ? 'text-[#00E701]' : 'text-white'}`}>
                      {lastResult.hits}/{selectedNumbers.length}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Controls & Payouts */}
          <div className="space-y-4">
            {/* Bet Amount */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <label className="text-[#A1A1AA] text-sm mb-2 block">Bet Amount</label>
              <Input
                type="number"
                value={betAmount}
                onChange={(e) => setBetAmount(Number(e.target.value))}
                disabled={loading}
                className="h-12 bg-black/20 border-white/10 text-white font-mono text-lg"
                data-testid="bet-input"
              />
              <div className="flex gap-2 mt-2">
                {[50, 100, 500].map(amt => (
                  <Button
                    key={amt}
                    variant="outline"
                    size="sm"
                    onClick={() => setBetAmount(amt)}
                    disabled={loading}
                    className="flex-1 border-white/10 text-white/60 hover:text-white text-xs"
                  >
                    ₹{amt}
                  </Button>
                ))}
              </div>
            </div>

            {/* Payout Table */}
            {selectedNumbers.length > 0 && (
              <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
                <h3 className="text-white font-bold mb-3">Payouts ({selectedNumbers.length} picks)</h3>
                <div className="space-y-1">
                  {Object.entries(payoutTable[selectedNumbers.length] || {}).map(([hits, mult]) => (
                    <div key={hits} className="flex justify-between text-sm">
                      <span className="text-[#A1A1AA]">{hits} hits</span>
                      <span className="text-[#00E701] font-bold">{mult}x</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Play Button */}
            <Button
              onClick={playGame}
              disabled={loading || betAmount <= 0 || betAmount > (user?.balance || 0) || selectedNumbers.length === 0}
              className="w-full h-14 bg-[#FF6B00] hover:bg-[#E65C00] text-black font-bold text-lg"
              data-testid="play-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  Drawing...
                </div>
              ) : (
                <>
                  <Zap className="w-5 h-5 mr-2" />
                  Play (₹{betAmount})
                </>
              )}
            </Button>

            {/* History */}
            <div className="p-4 rounded-xl bg-[#1A1B1F] border border-white/10">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <History className="w-4 h-4" /> Recent
              </h3>
              <div className="space-y-2">
                {history.map((h, i) => (
                  <div 
                    key={i}
                    className={`p-2 rounded text-xs ${
                      h.won ? 'bg-[#00E701]/20' : 'bg-white/5'
                    }`}
                  >
                    <div className="flex justify-between">
                      <span className="text-[#A1A1AA]">{h.hits} hits</span>
                      <span className={h.won ? 'text-[#00E701]' : 'text-[#FF3B30]'}>
                        {h.won ? `+₹${h.winnings.toFixed(0)}` : '-₹' + h.bet_amount}
                      </span>
                    </div>
                  </div>
                ))}
                {history.length === 0 && (
                  <span className="text-[#52525B] text-sm">No games yet</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
